import React, {
  useMemo,
} from "react";
import { Toolbar, Tabs, Tab } from "@material-ui/core";
import { useRouteMatch } from "react-router-dom";
import { makeStyles } from "@material-ui/styles";
import { Helmet } from "react-helmet";
import _, { isEmpty } from "lodash";
import { LinearProgress, Pill, Text, Button } from "../../components";
import { taskFields } from "../../schema/task";
import ResetConfirmationDialog from "./ResetConfirmationDialog";
import SaveTaskDialog from "./SaveTaskDialog";
import { usePushHistory } from "../../components/NavLink";
import TaskDefTasks from "./TaskDefTasks";
import { ArrowBack } from "@material-ui/icons";
import { IconButton } from "@material-ui/core";
import DefaultLoader from "../../components/DefaultLoader";
import SafeEditor from "../../components/SafeEditor";
import { useTaskDefinition } from "../../hooks/useTaskDefinition";
import { useLocation } from "react-router-dom";
import { navigateWorkflowTaskLink } from "./services/navigateWorkflowTaskLink";
const useStyles = makeStyles({
  wrapper: {
    display: "flex",
    height: "100%",
    alignItems: "stretch",
    flexDirection: "column",
  },
  name: {
    fontWeight: "bold",
  },
  rightButtons: {
    display: "flex",
    flexGrow: 1,
    justifyContent: "flex-end",
    gap: 8,

    "&>button": {
      margin: "0.5rem 0",
      padding: "4px 0",
      fontSize: "0.75rem",
      fontFamily: "Poppins, regular",
      lineHeight: "1.5",
    },
  },
  workflowDefTabs: {
    // marginBottom: "1rem",
  },
  workflowDefTabPannels: {
    height: "calc(100vh - 9rem)", //subtracted hieght for tab header and toolbar
    overflow: "auto",
  },
  main: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "auto",
    // backgroundColor: "#fff",
    // height: "100vh",
  },
  fields: {
    width: "100%",
    height: "100%",

    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflowX: "hidden",
    overflowY: "auto",
    gap: 15,
  },
});

export default function TaskDefinition(props) {
  const permissions = props?.permissions;
  const classes = useStyles();
  const match = useRouteMatch();
  const navigate = usePushHistory();
  const taskName = _.get(match, "params.name");
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const { loadedFrom, workflowName } = navigateWorkflowTaskLink.parseContext(params)
  // Use the custom hook for all task definition logic
  const {
    data,
    setData,
    isModified,
    jsonErrors,
    setJsonErrors,
    resetDialog,
    setResetDialog,
    saveDialog,
    setSaveDialog,
    tabIndex,
    isFetching,
    taskJson,
    handleTasks,
    handleOpenSave,
    handleSaveSuccess,
    doReset,
    handleValidate,
    handleEditorChange,
    handleTabChange,
    checkExistingTask,
    handleEditorWillMount,
    handleEditorDidMount,
  } = useTaskDefinition(taskName);

  const PermissionConfig = useMemo(() => {
    return {
      create_task: permissions?.actions?.create_task === "True",
    };
  }, [permissions]);

  const inputFields = taskFields;

  // Custom save success handler that includes navigation
  const handleSaveSuccessWithNavigation = (name, loadedFrom, workflowName) => {
    handleSaveSuccess(name, navigate, loadedFrom, workflowName);
  };

  return (
    <>
      <Helmet>
        <title>Conductor UI - Task Definition - {taskName || "New Task"}</title>
      </Helmet>

      <SaveTaskDialog
        document={saveDialog}
        onCancel={() => setSaveDialog(null)}
        loadedFrom={loadedFrom}
        workflowName={workflowName}
        onSuccess={handleSaveSuccessWithNavigation}
      />

      <ResetConfirmationDialog
        version={resetDialog}
        onConfirm={doReset}
        onClose={() => setResetDialog(false)}
      />

      {isFetching && <LinearProgress />}
      <div className={classes.wrapper}>
        <Toolbar>
          <IconButton
            color="primary"
            onClick={() => {
              navigate("/taskDefs");
            }}
          >
            <ArrowBack />
          </IconButton>
          <Text className={classes.name}>{taskName || "NEW"}</Text>

          {isModified ? (
            <Pill color="yellow" label="Modified" />
          ) : (
            <Pill label="Unmodified" />
          )}
          {!_.isEmpty(jsonErrors) && <Pill color="red" label="Validation" />}

          {PermissionConfig.create_task && (
            <div className={classes.rightButtons}>
              <Button
                disabled={!_.isEmpty(jsonErrors) || !isModified}
                onClick={handleOpenSave}
                variant="contained"
                color="primary"
              >
                {checkExistingTask() ? "Update" : "Create"}
              </Button>
              <Button
                onClick={() => setResetDialog(true)}
                variant="outlined"
                color="primary"
                disabled={!isModified}
              >
                Reset
              </Button>
            </div>
          )}
        </Toolbar>
        <div className={classes.workflowDefTabs}>
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            aria-label="basic tabs"
          >
            <Tab label="Task" />
            <Tab label="Code" />
          </Tabs>
        </div>
        <div
          className={classes.workflowDefTabPannels}
          hidden={tabIndex !== 0}
          value={tabIndex}
          index={1}
        >
          {isFetching || isEmpty(data) ? (
            <DefaultLoader
              displayModal={isFetching}
              message={`Loading ${taskName}`}
            />
          ) : (
            <TaskDefTasks
              inputFields={inputFields}
              data={data}
              setData={setData}
              handleTasks={handleTasks}
              jsonErrors={jsonErrors}
              setJsonErrors={setJsonErrors}
              taskName={taskName}
            />
          )}
        </div>
        <div
          className={classes.workflowDefTabPannels}
          hidden={tabIndex !== 1}
          value={tabIndex}
          index={0}
        >
          <SafeEditor
            height="100%"
            width="100%"
            theme="vs-light"
            language="json"
            value={taskJson}
            autoIndent={true}
            beforeMount={handleEditorWillMount}
            onMount={handleEditorDidMount}
            onValidate={handleValidate}
            // onChange={handleChange}
            onChange={(newValue, event) => handleEditorChange(newValue, event)}
            options={{
              readOnly: !PermissionConfig.create_task,
              smoothScrolling: true,
              selectOnLineNumbers: true,
              minimap: {
                enabled: false,
              },
            }}
          />
        </div>
      </div>
    </>
  );
}
